<script setup lang="ts">
import Modal from '@/Components/Molecules/Modal.vue';
import PriceDisplay from '@/Components/Molecules/PriceDisplay.vue';
import PricingTable from '@/Components/Molecules/PricingTable.vue';
import SelectButton from '@/Components/Molecules/SelectButton.vue';
import ToggleSwitch from '@/Components/Molecules/ToggleSwitch.vue';
import { usePriceCalculation } from '@/composables/usePriceCalculation';
import { useTranslations } from '@/composables/useTranslations';
import { usePage } from '@inertiajs/vue3';
import { computed, onMounted, ref, watch } from 'vue';

const takeoffIcon = '/wireframe/components/icons/takeoff.svg';
const landingIcon = '/wireframe/components/icons/landing.svg';
const passengersIcon = '/wireframe/components/icons/passengers.svg';

const emit = defineEmits<{
    (
        e: 'proceed',
        payload: {
            tripType: 'oneway' | 'roundtrip';
            from: string;
            dropTo: string;
            passengers: number;
            basePrice: number;
            nightPrice: number;
        },
    ): void;
}>();

const { t } = useTranslations();
const page = usePage();

const STORAGE_KEY = 'quickPrice.form';
const SHARED_TRIP_KEY = 'trip.shared';
const isBrowser = typeof window !== 'undefined';
const STATE_EXPIRY_HOURS = 0.1; // Durée de vie du state en heures

const tripType = ref<'oneway' | 'roundtrip'>('oneway');
const from = ref('');
const dropTo = ref('');
const passengers = ref(1);
const attemptedProceed = ref(false);
const showPricingModal = ref(false);

const locationsWithKeys = computed<[string, string][]>(() => {
    const translations = (page.props as any).translations || {};
    const locale = (page.props as any).locale || 'en';
    const currentTranslations = translations[locale] || translations.en || {};
    const locationsObj = (currentTranslations.locations || {}) as Record<string, string>;

    // Return array of [key, value] pairs
    return Object.entries(locationsObj);
});

const fromDisplayValue = computed<string>(() => {
    if (!from.value) return t('quickPrice.selectDeparture');
    const entry = locationsWithKeys.value.find(([key]) => key === from.value);
    return entry?.[1] ?? from.value;
});

const fromOptions = computed(() => {
    return [
        { value: '', label: t('quickPrice.selectDeparture') },
        ...locationsWithKeys.value.map(([key, label]) => ({
            value: key,
            label,
            disabled: dropTo.value === key,
        })),
    ];
});

const dropToDisplayValue = computed<string>(() => {
    if (!dropTo.value) return t('quickPrice.selectDestination');
    const entry = locationsWithKeys.value.find(([key]) => key === dropTo.value);
    return entry?.[1] ?? dropTo.value;
});

const dropToOptions = computed(() => {
    return [
        { value: '', label: t('quickPrice.selectDestination') },
        ...locationsWithKeys.value.map(([key, label]) => ({
            value: key,
            label,
            disabled: from.value === key,
        })),
    ];
});

const passengersDisplayValue = computed<string>(() => String(passengers.value));
const passengersOptions = computed(() => {
    return Array.from({ length: 16 }, (_, index) => {
        const value = String(index + 1);
        return { value, label: value };
    });
});

// Utilisation du composable pour calculer les prix
const { basePrice, nightPrice, priceDisplayQuickPrice } = usePriceCalculation({
    tripType,
    from,
    dropTo,
    passengers,
});

const canProceed = computed(() => {
    return from.value && dropTo.value && passengers.value > 0;
});

const isFromValid = computed(() => !!from.value);
const isDropToValid = computed(() => !!dropTo.value);

function persistSharedTrip() {
    if (!isBrowser) return;
    const payload = {
        tripType: tripType.value,
        from: from.value,
        dropTo: dropTo.value,
        passengers: passengers.value,
        timestamp: Date.now(),
    };
    window.localStorage.setItem(SHARED_TRIP_KEY, JSON.stringify(payload));
}

function saveState() {
    if (!isBrowser) return;
    const data = {
        tripType: tripType.value,
        from: from.value,
        dropTo: dropTo.value,
        passengers: passengers.value,
        timestamp: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    persistSharedTrip();
}

function loadState() {
    if (!isBrowser) return;
    const expiryTime = STATE_EXPIRY_HOURS * 60 * 60 * 1000;

    const parseIfFresh = (raw: string | null) => {
        if (!raw) return null;
        try {
            const data = JSON.parse(raw);
            if (!data?.timestamp) return data;
            const now = Date.now();
            if (now - data.timestamp > expiryTime) return null;
            return data;
        } catch {
            return null;
        }
    };

    const quickData = parseIfFresh(window.localStorage.getItem(STORAGE_KEY));
    const sharedData = parseIfFresh(window.localStorage.getItem(SHARED_TRIP_KEY));

    const data = sharedData || quickData;
    if (!data) return;

    if (data.tripType) tripType.value = data.tripType;
    if (typeof data.from === 'string') from.value = data.from;
    if (typeof data.dropTo === 'string') dropTo.value = data.dropTo;
    if (data.passengers) passengers.value = Number(data.passengers) || 1;
}

watch([tripType, from, dropTo, passengers], saveState, { deep: false });

onMounted(() => {
    loadState();
});

function proceedToBooking() {
    attemptedProceed.value = true;
    if (!canProceed.value) return;

    emit('proceed', {
        tripType: tripType.value,
        from: from.value,
        dropTo: dropTo.value,
        passengers: passengers.value,
        basePrice: basePrice.value,
        nightPrice: nightPrice.value,
    });
}
</script>

<template>
    <div class="mx-auto w-full max-w-4xl space-y-6 rounded-3xl bg-primary-light p-8 shadow-lg">
        <h2 class="mb-6 text-center text-3xl font-bold text-dark">{{ t('quickPrice.title') }}</h2>
        <!-- Switch One Way / Round Trip -->
        <ToggleSwitch
            :model-value="tripType"
            @update:model-value="tripType = $event as 'oneway' | 'roundtrip'"
            :options="[
                { label: t('quickPrice.oneWay'), value: 'oneway' },
                { label: t('quickPrice.roundTrip'), value: 'roundtrip' },
            ]"
        />

        <!-- From -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
                <SelectButton
                    :label="t('quickPrice.from')"
                    :value="fromDisplayValue"
                    :model-value="from"
                    :options="fromOptions"
                    :icon-src="takeoffIcon"
                    required
                    @update:model-value="from = $event"
                />
                <p v-if="attemptedProceed && !isFromValid" class="mt-1 text-xs text-red-600">{{ t('quickPrice.required.from') }}</p>
            </div>

            <!-- Drop To -->
            <div>
                <SelectButton
                    :label="t('quickPrice.dropTo')"
                    :value="dropToDisplayValue"
                    :model-value="dropTo"
                    :options="dropToOptions"
                    :icon-src="landingIcon"
                    required
                    @update:model-value="dropTo = $event"
                />
                <p v-if="attemptedProceed && !isDropToValid" class="mt-1 text-xs text-red-600">{{ t('quickPrice.required.to') }}</p>
            </div>
        </div>

        <!-- Passengers -->
        <div class="flex flex-row items-center justify-center">
            <SelectButton
                :label="t('quickPrice.passengers')"
                :value="passengersDisplayValue"
                :model-value="passengersDisplayValue"
                :options="passengersOptions"
                :icon-src="passengersIcon"
                required
                @update:model-value="passengers = Number($event)"
            />
        </div>

        <!-- Dynamic Price -->
        <PriceDisplay :priceDisplay="priceDisplayQuickPrice" :showNightInfo="true" card="quickPrice" />

        <!-- Proceed to booking -->
        <div class="mt-6 text-center">
            <button
                @click="proceedToBooking"
                :class="[
                    'btn btn-shadow text-lg font-bold',
                    canProceed ? 'bg-primary text-white hover:bg-primary-dark' : 'cursor-not-allowed bg-primary text-white opacity-75',
                ]"
            >
                {{ t('quickPrice.proceedToBooking') }}
            </button>
        </div>

        <div class="mt-4 flex flex-col items-center gap-3 border-t border-primary-medium pt-4">
            <button @click="showPricingModal = true" class="text-sm font-semibold text-primary underline transition hover:text-primary-dark">
                📋 {{ t('quickPrice.viewPricing') }}
            </button>
        </div>

        <!-- Pricing Modal -->
        <Modal :show="showPricingModal" @close="showPricingModal = false">
            <PricingTable />
        </Modal>
    </div>
</template>
